// Подписи, единицы и «к лучшему» для полей замера InBody — один источник для всех экранов вкладки.

import type { NormKey, ScanField, Segment, Status, WorkoutRelation } from './api'

export type FieldLabel = {
  label: string
  unit: string | null
  /** Какое изменение к лучшему; none — ни то ни другое. */
  better: 'up' | 'down' | 'none'
  digits: number
}

export const FIELD_LABELS: Record<ScanField, FieldLabel> = {
  score: { label: 'Оценка InBody', unit: null, better: 'up', digits: 0 },
  weight_kg: { label: 'Вес', unit: 'кг', better: 'none', digits: 1 },
  skeletal_muscle_kg: { label: 'Скелетные мышцы', unit: 'кг', better: 'up', digits: 1 },
  body_fat_pct: { label: 'Жир', unit: '%', better: 'down', digits: 1 },
  body_fat_kg: { label: 'Жировая масса', unit: 'кг', better: 'down', digits: 1 },
  total_body_water_l: { label: 'Вода', unit: 'л', better: 'none', digits: 1 },
  protein_kg: { label: 'Белок', unit: 'кг', better: 'up', digits: 1 },
  minerals_kg: { label: 'Минералы', unit: 'кг', better: 'none', digits: 2 },
  visceral_fat_level: { label: 'Висцеральный жир', unit: null, better: 'down', digits: 0 },
  bmi: { label: 'ИМТ', unit: null, better: 'none', digits: 1 },
  fat_free_mass_kg: { label: 'Безжировая масса', unit: 'кг', better: 'up', digits: 1 },
  bmr_kcal: { label: 'Основной обмен', unit: 'ккал', better: 'up', digits: 0 },
  daily_kcal: { label: 'Калории на день', unit: 'ккал', better: 'none', digits: 0 },
  ideal_weight_kg: { label: 'Идеальный вес', unit: 'кг', better: 'none', digits: 1 },
  to_ideal_weight_kg: { label: 'До идеала: вес', unit: 'кг', better: 'none', digits: 1 },
  to_ideal_fat_kg: { label: 'До идеала: жир', unit: 'кг', better: 'none', digits: 1 },
  to_ideal_muscle_kg: { label: 'До идеала: мышцы', unit: 'кг', better: 'none', digits: 1 },
}

/** Группы полей на сверке и в карточке замера (R06): основное, состав, энергия и цели. */
export const FIELD_GROUPS: { id: string; title: string; fields: ScanField[] }[] = [
  { id: 'main', title: 'Основное', fields: ['weight_kg', 'score'] },
  {
    id: 'composition',
    title: 'Состав',
    fields: [
      'skeletal_muscle_kg',
      'body_fat_pct',
      'body_fat_kg',
      'fat_free_mass_kg',
      'total_body_water_l',
      'protein_kg',
      'minerals_kg',
      'visceral_fat_level',
      'bmi',
    ],
  },
  {
    id: 'energy',
    title: 'Энергия и цели',
    fields: ['bmr_kcal', 'daily_kcal', 'ideal_weight_kg', 'to_ideal_weight_kg', 'to_ideal_fat_kg', 'to_ideal_muscle_kg'],
  },
]

export const NORM_LABELS: Record<NormKey, string> = {
  weight_kg: FIELD_LABELS.weight_kg.label,
  skeletal_muscle_kg: FIELD_LABELS.skeletal_muscle_kg.label,
  body_fat_pct: FIELD_LABELS.body_fat_pct.label,
  total_body_water_l: FIELD_LABELS.total_body_water_l.label,
  protein_kg: FIELD_LABELS.protein_kg.label,
  minerals_kg: FIELD_LABELS.minerals_kg.label,
  visceral_fat_level: FIELD_LABELS.visceral_fat_level.label,
  bmi: FIELD_LABELS.bmi.label,
  fat_free_mass_kg: FIELD_LABELS.fat_free_mass_kg.label,
}

export const SEGMENT_LABELS: Record<Segment, string> = {
  left_arm: 'Левая рука',
  right_arm: 'Правая рука',
  trunk: 'Корпус',
  left_leg: 'Левая нога',
  right_leg: 'Правая нога',
}

export const SEGMENT_KIND_LABELS = { lean: 'Мышцы по сегментам', fat: 'Жир по сегментам' } as const

export const STATUS_LABELS: Record<Status, string> = { below: 'ниже нормы', normal: 'норма', above: 'выше нормы' }

export const SEX_LABELS = { M: 'мужской', F: 'женский' } as const

export const RELATION_LABELS: Record<WorkoutRelation, string> = {
  before: 'до тренировки',
  after: 'после тренировки',
  none: 'тренировки не было',
}

export function fastedLabel(value: boolean | null | undefined): string {
  if (value === true) return 'натощак'
  if (value === false) return 'не натощак'
  return 'натощак — не помню'
}

export function relationLabel(value: WorkoutRelation | null | undefined): string {
  return value ? RELATION_LABELS[value] : 'тренировка — не помню'
}
